import { DirectionProvider } from '@radix-ui/react-direction'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { CookieConsent } from '@/components/CookieConsent'

import packageJson from '../package.json'
import { useCurrentUser } from './api/auth/queries/useCurrentUser'
import { useAppConfig } from './api/config/queries/useAppConfig'
//utils
import { currentAuthenticatedUser } from './api/user/userService'
import { ImpersonationBanner } from './components/ImpersonationBanner'
import { LoadingScreen } from './components/LoadingScreen'
import { MaintenanceScreen } from './components/MaintenanceScreen'
import { ToastContainer, ToastProvider } from './components/ui/toast'
import Layout from './Layout'
import { claritySet, claritySetUser } from './lib/clarity'
import { useImpersonatedUserId } from './lib/impersonation'
import { QUERY_KEYS } from './lib/queryKeys'
import NavBar from './NavBar'
import OfficeNavbar from './pages/office-side/OfficeNavbar'
import { OfficeRoutePath, RoutePath } from './RoutePath'
import { setIsAuthenticated } from './store/appSlice'

const getPostLoginRoute = (permissions?: Record<string, boolean>) => {
  if (permissions?.AffiliateManager) return OfficeRoutePath.AffiliateManager
  if (permissions?.PickupOrderDriver) return OfficeRoutePath.ScanPickupOrders
  return RoutePath.ClientAddresses
}

export default function App() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isReady, setIsReady] = useState(false)
  const { data: loggedUser } = useCurrentUser()
  const { data: appConfig, isLoading: isAppConfigLoading, error: appConfigError } = useAppConfig()
  const isMaintenance =
    !loggedUser?.permissions?.['Admin'] && (appConfig?.isMaintenance || (appConfigError as AxiosError)?.response?.status === 503)

  const location = useLocation()
  const { i18n } = useTranslation()

  const isOfficePage = location.pathname.startsWith('/office') || location.pathname.startsWith('/all-packages-view')
  const impersonatedUserId = useImpersonatedUserId()
  const showImpersonationBanner =
    !!impersonatedUserId && (location.pathname.startsWith('/payment') || location.pathname.startsWith('/receipt-upload'))
  const shouldShowLoading = !isReady || isAppConfigLoading

  const clientDir = i18n.dir()
  const clientLang = i18n.language

  useEffect(() => {
    if (isOfficePage) {
      i18n.changeLanguage('en')
      document.documentElement.lang = 'en'
      document.documentElement.dir = 'ltr'
    } else {
      i18n.changeLanguage('he')
      document.documentElement.lang = clientLang
      document.documentElement.dir = clientDir
    }
  }, [isOfficePage, clientLang, clientDir, i18n])

  useEffect(() => {
    if (window.location.href.includes('office')) {
      const enableToolbar = document.getElementById('enable-toolbar')
      if (enableToolbar) enableToolbar.style.display = 'none'
    }
  }, [])

  useEffect(() => {
    //versioning
    const version = packageJson.version
    const last_version = localStorage.getItem(`version`)
    if (!last_version || last_version !== version) {
      localStorage.setItem(`version`, version)
      window.location.reload()
    }

    //user auth
    ;(async () => {
      const userData = await currentAuthenticatedUser()
      if (!userData) {
        localStorage.removeItem('cognito-tokens')
        dispatch(setIsAuthenticated(false))
        setIsReady(true)
        return
      }
      queryClient.setQueryData([QUERY_KEYS.LOGGED_USER], userData)
      dispatch(setIsAuthenticated(true))
      if (location.pathname == '/') navigate(getPostLoginRoute(userData?.permissions))
      else navigate(location.pathname + location.search + location.hash)
      setIsReady(true)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Set Sentry user context when user is logged in
  useEffect(() => {
    if (loggedUser?.id) {
      Sentry.setUser({
        id: loggedUser.id,
        email: loggedUser.email,
        username: loggedUser.name || loggedUser.id,
      })
    } else {
      Sentry.setUser(null)
    }
  }, [loggedUser])

  useEffect(() => {
    if (loggedUser?.id) {
      claritySetUser({ id: loggedUser.id, email: loggedUser.email })
    } else {
      claritySetUser(null)
    }
    claritySet('environment', import.meta.env.MODE ?? 'development')
  }, [loggedUser?.id, loggedUser?.email])

  const hardRefresh = () => {
    localStorage.clear()
    navigate('/')
    window.location.reload()
  }

  return (
    <DirectionProvider dir={isOfficePage ? 'ltr' : clientDir}>
      <ToastProvider>
        <div className='h-[100dvh]'>
          {isMaintenance ? (
            <MaintenanceScreen />
          ) : shouldShowLoading ? (
            <LoadingScreen onRefresh={hardRefresh} />
          ) : (
            <>
              {loggedUser?.permissions['Admin'] ||
              loggedUser?.permissions['Agent'] ||
              loggedUser?.permissions['SeniorAgent'] ||
              loggedUser?.permissions['OfficeManager'] ||
              loggedUser?.permissions['Worker'] ||
              loggedUser?.permissions['PickupOrderDriver'] ||
              loggedUser?.permissions['AffiliateManager'] ? (
                <OfficeNavbar />
              ) : (
                <NavBar />
              )}
              {showImpersonationBanner ? <ImpersonationBanner /> : null}
              <Layout />
            </>
          )}
        </div>
        <ToastContainer />
        <CookieConsent />
      </ToastProvider>
    </DirectionProvider>
  )
}
